/**
 * Full multi-line chip — Week view and sparse Month days. Type scale and
 * padding grow in Week view.
 */
import type { CSSProperties } from 'react';

import { PrivateEventBadge } from './PrivateEventBadge';
import type { ChipColors } from './workshops-calendar-chip-shared';
import {
  chipEdgeStyle,
  chipStateLine,
  MONO,
  venueNameStyle,
} from './workshops-calendar-chip-shared';
import type { WorkshopsCalendarChipCompactProps } from './WorkshopsCalendarChipCompact';
import { WorkshopsCalendarChipTreeIcon } from './WorkshopsCalendarChipTreeIcon';

export interface WorkshopsCalendarChipFullProps extends WorkshopsCalendarChipCompactProps {
  view: 'Month' | 'Week';
  hovered: boolean;
}

/** Type scale + padding for the full chip — larger in Week view. */
function fullChipSizing(view: 'Month' | 'Week') {
  const wk = view === 'Week';
  return {
    venueFs: wk ? 12.5 : 10,
    speciesFs: wk ? 11.5 : 9.5,
    metaFs: wk ? 11 : 9,
    staffFs: wk ? 10.5 : 8.5,
    pad: wk ? '7px 9px' : '4px 6px',
    gap: wk ? 3 : 1,
  };
}

interface FullChipStyles {
  metaFs: number;
  container: CSSProperties;
  venue: CSSProperties;
  species: CSSProperties;
  teacher: CSSProperties;
  metaRow: CSSProperties;
  num: CSSProperties;
  repl: CSSProperties;
}

function fullChipStyles(
  view: 'Month' | 'Week',
  colors: ChipColors,
  hovered: boolean,
  clickable: boolean,
  edge: CSSProperties['borderLeftStyle'],
): FullChipStyles {
  const { venueFs, speciesFs, metaFs, staffFs, pad, gap } = fullChipSizing(view);
  return {
    metaFs,
    container: {
      position: 'relative',
      marginTop: 2,
      padding: pad,
      background: colors.bg,
      borderLeft: `4px ${edge} ${colors.border}`,
      borderRadius: 3,
      boxShadow: hovered ? `0 2px 6px rgba(0,0,0,.13), 0 0 0 1px ${colors.border}` : colors.shadow,
      cursor: clickable ? 'pointer' : 'default',
      transform: hovered && clickable ? 'translateY(-1px)' : 'none',
      transition: 'box-shadow .12s, transform .12s',
      display: 'flex',
      flexDirection: 'column',
      gap,
      minWidth: 0,
    },
    venue: {
      fontSize: venueFs,
      fontWeight: 700,
      color: 'var(--nb-ink)',
      lineHeight: 1.2,
      wordBreak: 'break-word',
    },
    species: {
      fontSize: speciesFs,
      color: 'var(--nb-muted)',
      lineHeight: 1.2,
      wordBreak: 'break-word',
    },
    teacher: { fontSize: staffFs, lineHeight: 1.3, wordBreak: 'break-word' },
    metaRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 6,
      marginTop: 1,
      lineHeight: 1.15,
    },
    num: { ...MONO, fontSize: metaFs, fontWeight: 600, color: 'var(--nb-ink)' },
    repl: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      ...MONO,
      fontSize: metaFs,
      fontWeight: 600,
      color: 'var(--nb-primary)',
    },
  };
}

export function WorkshopsCalendarChipFull({
  w,
  timeLabel,
  colors,
  view,
  hovered,
  clickable,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: WorkshopsCalendarChipFullProps) {
  const s = fullChipStyles(view, colors, hovered, clickable, chipEdgeStyle(w));
  const state = chipStateLine(w);
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      style={s.container}
    >
      {/* Corner marker rather than sharing the venue-name element, so the
          badge never disturbs the name's own text/click target. */}
      {w.isPrivate && (
        <PrivateEventBadge
          fontSize={s.metaFs - 1}
          style={{ position: 'absolute', top: 4, right: 6 }}
        />
      )}
      {state && (
        <div style={{ ...s.species, color: state.color, fontWeight: 700 }}>{state.label}</div>
      )}
      <div style={{ ...s.venue, ...venueNameStyle(w), paddingRight: w.isPrivate ? 46 : 0 }}>
        {w.venueName}
      </div>
      <div style={s.species}>{w.species ? `${w.species} – ${timeLabel}` : timeLabel}</div>
      {w.leadInstructor && (
        <div style={s.teacher}>
          <span style={{ color: 'var(--nb-faint)' }}>Teacher: </span>
          <span style={{ color: 'var(--nb-ink)', fontWeight: 600 }}>{w.leadInstructor}</span>
        </div>
      )}
      {w.assistant && (
        <div style={s.teacher}>
          <span style={{ color: 'var(--nb-faint)' }}>Assistant: </span>
          <span style={{ color: 'var(--nb-subtle)' }}>{w.assistant}</span>
        </div>
      )}
      <div style={s.metaRow}>
        <span style={s.num}>
          {w.booked}/{w.capacity}
        </span>
        {(w.replacements ?? 0) > 0 && (
          <span
            title={`${w.replacements} tree replacement${w.replacements === 1 ? '' : 's'}`}
            style={s.repl}
          >
            {w.replacements}
            <WorkshopsCalendarChipTreeIcon size={s.metaFs + 1} />
          </span>
        )}
      </div>
    </div>
  );
}
